import React, { useState } from 'react'
import {
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  GridItem,
  Stack,
  useColorModeValue,
  Heading,
  Checkbox,
  Link,
  chakra,
  Box,
  FormHelperText,
} from '@chakra-ui/react'
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  Path,
} from 'react-hook-form'
import { registerFields } from 'source-of-truth/registerFields'
import { IRegisterInput } from 'interfaces/register.interface'
import { HiEye, HiEyeOff } from 'react-icons/hi'
import { RegisterSchemaType } from 'schemas/register.schema'

export interface IRegisterFormProps {
  watch?: UseFormWatch<any>
  errors: FieldErrors
  register: UseFormRegister<any>
  readonlyInputs?: Path<RegisterSchemaType>[]
}

function RegisterForm(props: IRegisterFormProps) {
  const labelColor = useColorModeValue('gray.700', 'gray.50')

  const [showPassword, setShowPassword] = useState(false)

  return (
    <Stack
      px={4}
      py={5}
      p={[null, 6]}
      bg={useColorModeValue('white', 'gray.700')}
      spacing={6}
    >
      <Heading fontSize='xl'>Crea tu cuenta en Autoinspector</Heading>
      <SimpleGrid columns={6} spacing={6}>
        {registerFields.map((input) => {
          const readonly =
            props.readonlyInputs && props.readonlyInputs.includes(input.name)

          return (
            <FormControl
              key={input.name}
              as={GridItem}
              colSpan={input.colSpan}
              isInvalid={props.errors[input.name] && !!props.errors[input.name]}
              isDisabled={readonly}
            >
              <FormLabel
                htmlFor={input.name}
                fontSize='sm'
                fontWeight='md'
                color={labelColor}
              >
                {input.label}
              </FormLabel>
              {input.name === 'password' ? (
                <InputGroup size='md'>
                  <Input
                    {...props.register(input.name)}
                    type={showPassword ? 'text' : input.type}
                    id={input.name}
                    focusBorderColor='landingPrimary'
                    shadow='sm'
                    size='md'
                    w='full'
                    rounded='landing'
                    readOnly={readonly}
                    isDisabled={readonly}
                  />
                  <InputRightElement width='4.5rem'>
                    <IconButton
                      h='1.75rem'
                      size='sm'
                      _focus={{ boxShadow: 'none' }}
                      ml='4'
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <HiEye /> : <HiEyeOff />}
                      aria-label='Show password'
                    />
                  </InputRightElement>
                </InputGroup>
              ) : (
                <Input
                  {...props.register(input.name)}
                  type={input.type}
                  id={input.name}
                  focusBorderColor='landingPrimary'
                  shadow='sm'
                  size='md'
                  w='full'
                  rounded='landing'
                  readOnly={readonly}
                />
              )}
              {input.helper && (
                <FormHelperText fontSize='xs'>{input.helper}</FormHelperText>
              )}
            </FormControl>
          )
        })}
        <FormControl as={GridItem} colSpan={6}>
          <Box display='flex' alignItems='center'>
            <Checkbox
              {...props.register('acceptTerms')}
              mr='2'
              isInvalid={
                props.errors['acceptTerms'] && !!props.errors['acceptTerms']
              }
            ></Checkbox>
            <chakra.span fontSize='xs'>
              Con este registro he leído y acepto los{' '}
              <Link
                isExternal
                href='https://autoinspector.ai/legal/general-terms'
                color='landingPrimary'
              >
                Términos y Condiciones de Uso
              </Link>
              ,{' '}
              <Link
                isExternal
                href='https://autoinspector.ai/legal/sales-terms'
                color='landingPrimary'
              >
                Términos de venta
              </Link>
              ,{' '}
              <Link
                isExternal
                href='https://autoinspector.ai/legal/privacy-policy'
                color='landingPrimary'
              >
                Política de Privacidad
              </Link>
              ,{' '}
              <Link
                isExternal
                href='https://autoinspector.ai/legal/sla'
                color='landingPrimary'
              >
                Acuerdo de Nivel de Servicio
              </Link>{' '}
              y{' '}
              <Link
                isExternal
                href='https://autoinspector.ai/legal/client-nda'
                color='primary'
              >
                Acuerdo de Confidencialidad
              </Link>
            </chakra.span>
          </Box>
        </FormControl>
      </SimpleGrid>
    </Stack>
  )
}

export default RegisterForm
